import { useEffect, useState } from 'react';
import { DataGrid } from '../../components/grid/DataGrid';
import { Icona } from '../../components/Icona';
import { scontrini } from '../../api/scontrini';
import type { DettaglioScontrino } from '../../api/scontrini';
import { apri, chiudi, idDallAncora } from '../../lib/ancora';
import { formattaData, formattaDataOra, formattaImporto } from '../../lib/formato';

const INTESTAZIONI: Record<string, string> = {
  EmessoIl: 'Emesso il',
  PrezzoCambiato: 'Prezzo cambiato',
  MailCliente: 'Mail cliente',
  NumeroDocumento: 'Doc. RT',
};

/*  LO STORICO DEGLI SCONTRINI (23/10/2026), in Amministrazione. Una griglia come le altre:
    si filtra per data, tecnico, pagamento, stato e articolo (che e' un testo: «contiene»),
    e il totale a pie' di colonna e' la somma di quello che si sta guardando. Una riga
    apre lo scontrino, con le azioni per quelli rimasti a meta'.  */
export function StoricoScontrini() {
  const [aperto, setAperto] = useState<string | null>(() => idDallAncora('storico'));
  const [versione, setVersione] = useState(0);

  useEffect(() => {
    const rileggi = () => {
      const id = idDallAncora('storico');
      if (id !== null) setAperto(id);
    };
    window.addEventListener('hashchange', rileggi);
    return () => window.removeEventListener('hashchange', rileggi);
  }, []);

  return (
    <section className="pagina con-scheda">
      <div className="colonna-principale">
        <header className="testata-pagina">
          <div>
            <h1>Storico scontrini</h1>
            <p>
              Tutti gli scontrini dei tecnici caldaie. Si filtra per data, tecnico, pagamento e articolo dalle
              intestazioni; «Prezzo cambiato» dice dove il tecnico non ha tenuto il prezzo del listino.
            </p>
          </div>
        </header>

        <DataGrid
          nomeGriglia="scontrini"
          versione={versione}
          colonneVisibili={[
            'Numero',
            'Data',
            'EmessoIl',
            'Tecnico',
            'Pagamento',
            'Stato',
            'Articoli',
            'PrezzoCambiato',
            'Totale',
            'MailCliente',
            'NumeroDocumento',
          ]}
          intestazioni={INTESTAZIONI}
          pastiglie={['Stato']}
          idSelezionato={aperto}
          onRigaSelezionata={(riga) => {
            const id = riga['Id'];
            if (typeof id !== 'string') return;
            setAperto(id);
            apri('storico', id);
          }}
        />
      </div>

      {aperto && (
        <SchedaScontrino
          id={aperto}
          onChiudi={() => {
            setAperto(null);
            chiudi('storico');
          }}
          onCambiato={() => setVersione((v) => v + 1)}
        />
      )}
    </section>
  );
}

const STATI: Record<string, string> = {
  DaEmettere: 'Da emettere',
  Emesso: 'Emesso',
  DaVerificare: 'Da verificare',
  Annullato: 'Annullato',
};

function SchedaScontrino({
  id,
  onChiudi,
  onCambiato,
}: {
  id: string;
  onChiudi: () => void;
  onCambiato: () => void;
}) {
  const [s, setS] = useState<DettaglioScontrino | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [esito, setEsito] = useState<string | null>(null);

  useEffect(() => {
    setEsito(null);
    setS(null);
    setErrore(null);
    scontrini
      .leggi(id)
      .then(setS)
      .catch((e: Error) => setErrore(e.message));
  }, [id]);

  async function fai(azione: () => Promise<DettaglioScontrino>) {
    setErrore(null);
    try {
      setS(await azione());
      onCambiato();
    } catch (e) {
      setErrore((e as Error).message);
    }
  }

  return (
    <aside className="scheda" aria-label={`Scontrino ${s?.numero ?? ''}`}>
      <header>
        <div>
          <p className="chiave">
            <strong>{s?.numero ?? '…'}</strong> {s && <span className="stato">{STATI[s.stato] ?? s.stato}</span>}
          </p>
          <h2>{s ? `${s.tecnico} — ${formattaImporto(s.totale)} in ${s.pagamento === 'Contanti' ? 'contanti' : 'POS'}` : ''}</h2>
        </div>
        <button type="button" className="chiudi" onClick={onChiudi} aria-label="Chiudi la scheda">
          <Icona nome="chiudi" />
        </button>
      </header>

      {errore && <p className="errore">{errore}</p>}

      {s && (
        <>
          <p className="riferimenti">
            Del {formattaData(s.data)} · scritto {formattaDataOra(s.creatoIl)}
            {s.emessoIl && <> · emesso {formattaDataOra(s.emessoIl)}</>}
            {s.numeroDocumento && (
              <> · documento RT n. {s.numeroDocumento}{s.numeroChiusura ? ` (chiusura ${s.numeroChiusura})` : ''}</>
            )}
            {s.tentativi > 1 && <> · {s.tentativi} tentativi</>}
          </p>

          <div className="tabella-contenitore">
            <table>
              <thead>
                <tr>
                  <th>Articolo</th>
                  <th className="destra">Quantità</th>
                  <th className="destra">Prezzo</th>
                  <th className="destra">Listino</th>
                  <th className="destra">Importo</th>
                </tr>
              </thead>
              <tbody>
                {s.righe.map((r) => (
                  <tr key={r.codiceArticolo}>
                    <td>
                      {r.descrizione} <span className="nota">{r.codiceArticolo}</span>
                    </td>
                    <td className="destra">{r.quantita}</td>
                    <td className="destra">{formattaImporto(r.prezzoUnitario)}</td>
                    <td
                      className={
                        r.prezzoListino !== null && r.prezzoListino !== r.prezzoUnitario ? 'destra differenza' : 'destra'
                      }
                    >
                      {r.prezzoListino === null ? '—' : formattaImporto(r.prezzoListino)}
                    </td>
                    <td className="destra">{formattaImporto(r.importo)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4}>Totale, IVA {s.aliquotaIva}% compresa</td>
                  <td className="destra">
                    <strong>{formattaImporto(s.totale)}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <p className="note">
            Copia per mail a {s.mailCopia ?? '—'}
            {s.mailCliente === null && ' (il cliente non ha lasciato la mail: copia al tecnico)'}
            {s.copiaAccodataIl && <> · partita {formattaDataOra(s.copiaAccodataIl)}</>}
          </p>
          {s.ultimoErrore && <p className="errore">{s.ultimoErrore}</p>}
          {esito && <p className="messaggio">{esito}</p>}
          {s.motivoAnnullamento && <p className="note">Annullato: {s.motivoAnnullamento}</p>}

          <p className="azioni">
            {s.stato === 'DaVerificare' && (
              <>
                <button
                  type="button"
                  className="primario"
                  onClick={() =>
                    void fai(async () => {
                      const r = await scontrini.verifica(s.id);
                      setEsito(r.messaggio);
                      return r.scontrino;
                    })
                  }
                >
                  Controlla sulla stampante
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const numero = window.prompt('Numero del documento commerciale sulla stampante:');
                    if (numero !== null) void fai(() => scontrini.uscito(s.id, numero.trim() || null));
                  }}
                >
                  È uscito
                </button>
                <button type="button" onClick={() => void fai(() => scontrini.nonUscito(s.id))}>
                  Non è uscito
                </button>
              </>
            )}
            {s.stato === 'DaEmettere' && (
              <>
                <button type="button" className="primario" onClick={() => void fai(() => scontrini.riprova(s.id))}>
                  Riprova
                </button>
                <button
                  type="button"
                  className="pericolo"
                  onClick={() => {
                    const motivo = window.prompt('Perché si annulla?');
                    if (motivo?.trim()) void fai(() => scontrini.annulla(s.id, motivo.trim()));
                  }}
                >
                  Annulla
                </button>
              </>
            )}
          </p>
        </>
      )}
    </aside>
  );
}
